import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Sparkles, Home, Users, Waves, ChefHat, Car, Wifi } from 'lucide-react';
import SEO from '../components/SEO';

const LocationVillaLuxe = () => {
  return (
    <>
      <SEO 
        title="Location Villa de Luxe Île de Ré | Villa Nomade 4 Chambres avec Spa"
        description="Villa de luxe haut de gamme à l'Île de Ré : 4 chambres, spa de nage, jardin paysager, prestations premium. Séjour d'exception aux Portes-en-Ré."
        keywords="location villa luxe Île de Ré, villa haut de gamme Ré, villa avec spa Île de Ré, location prestige Charente-Maritime, villa 4 chambres Ré"
        ogImage="https://www.villanomadelesportesenre.com/medias/images/17.jpg"
        url="/location-villa-luxe-ile-de-re"
      />

      {/* Hero */}
      <div className="relative h-[70vh] min-h-[500px]">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(/images/villa/villa-bedroom-v2.jpg)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/70"></div>
        </div>
        <div className="relative h-full flex items-center justify-center text-white px-4">
          <div className="text-center max-w-4xl">
            <Sparkles className="w-16 h-16 mx-auto mb-6" />
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Villa de Luxe Île de Ré
            </h1>
            <p className="text-xl sm:text-2xl text-gray-200 mb-8">
              Séjour d'exception dans une villa contemporaine haut de gamme
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-[#c17a3a] hover:bg-[#a66830] text-white text-lg px-8 py-6">
                Demander un devis personnalisé
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-16">
        {/* Introduction Premium */}
        <section className="mb-16 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Villa Nomade - L'Excellence à l'Île de Ré</h2>
          <p className="text-lg text-gray-700 leading-relaxed max-w-4xl mx-auto mb-8">
            La <strong>Villa Nomade</strong> incarne le raffinement et le confort absolu pour un séjour d'exception 
            sur l'<strong>Île de Ré</strong>. Architecture contemporaine, prestations haut de gamme et emplacement privilégié 
            aux <strong>Portes-en-Ré</strong> font de cette villa une adresse unique pour des vacances inoubliables.
          </p>
        </section>

        {/* Caractéristiques Premium */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Prestations d'Exception</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-xl border-2 border-amber-200 text-center">
              <Home className="w-12 h-12 text-[#c17a3a] mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-3">200m² Spacieux</h3>
              <p className="text-gray-700">
                Villa contemporaine de plain-pied avec volumes généreux, baies vitrées panoramiques 
                et espaces de vie fluides ouverts sur le jardin paysager.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-xl border-2 border-amber-200 text-center">
              <Waves className="w-12 h-12 text-[#c17a3a] mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-3">Spa de Nage Chauffé</h3>
              <p className="text-gray-700">
                Spa de nage 10x3m chauffé toute l'année, avec jets de massage et nage à contre-courant. 
                L'équipement premium pour votre bien-être.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-xl border-2 border-amber-200 text-center">
              <Users className="w-12 h-12 text-[#c17a3a] mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-3">4 Chambres Luxueuses</h3>
              <p className="text-gray-700">
                3 chambres doubles haut de gamme et 1 chambre lits simples. 2 salles de douche privatives. 
                Literie premium pour 8 personnes.
              </p>
            </div>
          </div>
        </section>

        {/* Équipements détaillés */}
        <section className="mb-16 bg-gradient-to-br from-amber-50 to-orange-50 p-8 rounded-lg">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Équipements & Services Haut de Gamme</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <ChefHat className="w-6 h-6 text-[#c17a3a]" />
                Cuisine & Séjour
              </h3>
              <ul className="space-y-2 text-gray-700">
                <li>✓ Cuisine américaine équipée (Miele, Nespresso)</li>
                <li>✓ Îlot central avec plan de travail en granit</li>
                <li>✓ Cave à vin, four vapeur, lave-vaisselle</li>
                <li>✓ Séjour cathédrale avec cheminée design</li>
                <li>✓ Canapés de designer, mobilier contemporain</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Wifi className="w-6 h-6 text-[#c17a3a]" />
                Technologie & Confort
              </h3>
              <ul className="space-y-2 text-gray-700">
                <li>✓ Fibre optique haut débit (500 Mbps)</li>
                <li>✓ Smart TV 65" dans le séjour</li>
                <li>✓ Climatisation réversible toutes pièces</li>
                <li>✓ Système audio Sonos multi-room</li>
                <li>✓ Alarme et vidéosurveillance sécurisées</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Home className="w-6 h-6 text-[#c17a3a]" />
                Extérieurs
              </h3>
              <ul className="space-y-2 text-gray-700">
                <li>✓ Jardin paysager 800m² entièrement clos</li>
                <li>✓ Terrasses en bois exotique (80m²)</li>
                <li>✓ Salon de jardin teck 10 personnes</li>
                <li>✓ Barbecue Weber gaz haut de gamme</li>
                <li>✓ Bains de soleil premium et parasols</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Car className="w-6 h-6 text-[#c17a3a]" />
                Services Inclus
              </h3>
              <ul className="space-y-2 text-gray-700">
                <li>✓ Parking privé 2 voitures</li>
                <li>✓ Linge de maison premium fourni</li>
                <li>✓ Ménage de fin de séjour inclus</li>
                <li>✓ Accueil personnalisé avec dossier découverte</li>
                <li>✓ Conciergerie sur demande</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Emplacement privilégié */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Emplacement Privilégié aux Portes-en-Ré</h2>
          <div className="bg-white p-8 rounded-lg shadow-lg border border-gray-200">
            <p className="text-lg text-gray-700 mb-6">
              Située dans le village authentique des <strong>Portes-en-Ré</strong>, au nord de l'île, 
              la Villa Nomade bénéficie d'un emplacement idéal :
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-lg mb-3">🏖️ Proximité Immédiate</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• Port et commerces : 5 min à pied</li>
                  <li>• Plage de la Redoute : 2 km (5 min vélo)</li>
                  <li>• Plage Trousse-Chemise : 3 km</li>
                  <li>• Location vélos : 200m</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-3">🚴 Explorer l'Île</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• Saint-Martin-de-Ré : 8 km</li>
                  <li>• Phare des Baleines : 9 km</li>
                  <li>• Golf de Trousse-Chemise : accès à pied (en bordure)</li>
                  <li>• La Rochelle : 35 km</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Tarifs Premium */}
        <section className="mb-16 bg-gray-900 text-white p-12 rounded-lg">
          <h2 className="text-3xl font-bold mb-6 text-center">Tarifs & Disponibilités</h2>
          <p className="text-center text-gray-300 mb-8 text-lg">
            Location à la semaine (samedi à samedi) en haute saison. Courts séjours possibles hors saison.
          </p>
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white/10 p-6 rounded-lg text-center">
              <div className="text-sm text-gray-400 mb-2">HAUTE SAISON</div>
              <div className="text-3xl font-bold mb-2">2 800€</div>
              <div className="text-sm text-gray-300">Juillet - Août</div>
            </div>
            <div className="bg-white/10 p-6 rounded-lg text-center">
              <div className="text-sm text-gray-400 mb-2">MOYENNE SAISON</div>
              <div className="text-3xl font-bold mb-2">2 200€</div>
              <div className="text-sm text-gray-300">Juin - Septembre</div>
            </div>
            <div className="bg-white/10 p-6 rounded-lg text-center">
              <div className="text-sm text-gray-400 mb-2">BASSE SAISON</div>
              <div className="text-3xl font-bold mb-2">1 600€</div>
              <div className="text-sm text-gray-300">Octobre - Mai</div>
            </div>
          </div>
          <div className="text-center">
            <Link to="/contact">
              <Button size="lg" className="bg-[#c17a3a] hover:bg-[#a66830] text-white">
                Demander les disponibilités
              </Button>
            </Link>
          </div>
        </section>

        {/* CTA Final */}
        <section className="text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Réservez Votre Séjour d'Exception</h2>
          <p className="text-lg text-gray-700 mb-8 max-w-3xl mx-auto">
            Offrez-vous le luxe d'un séjour inoubliable dans notre villa d'exception. 
            Disponibilités limitées - Réservation anticipée recommandée.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/contact">
              <Button size="lg" className="bg-[#c17a3a] hover:bg-[#a66830] text-white">
                Réserver maintenant
              </Button>
            </Link>
            <Link to="/photos">
              <Button size="lg" variant="outline">
                Visiter la villa en photos
              </Button>
            </Link>
          </div>
        </section>
      </div>

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LodgingBusiness",
          "name": "Villa Nomade - Location Luxe Île de Ré",
          "description": "Villa de luxe 4 chambres avec spa de nage aux Portes-en-Ré",
          "priceRange": "€€€€",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Les Portes-en-Ré",
            "postalCode": "17880",
            "addressCountry": "FR"
          },
          "starRating": {
            "@type": "Rating",
            "ratingValue": "5"
          }
        })}
      </script>
    </>
  );
};

export default LocationVillaLuxe;